import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { getLastViewportPracticeId } from 'configuredAxios';

import { pm } from 'lib/helpers/utility';
import { useSmartNavigate } from 'lib/hooks/useSmartNavigate';
import { getPortalRoutePermission } from 'lib/helpers/routePermissions';

import Spinner from 'components/shared/spinner/spinner';
import { current_admin } from 'components/admin/lib/adminState';
import { currentPractice } from 'components/practice/practiceState';
import { permissions as permissionsState, userState as userStateAtom } from 'components/state';
import useUserPreferencesState from 'components/practice/UserPreferences/hooks/useUserPreferencesState';

const PrivateRoutes = ({ type = '' }) => {
  const userState = useRecoilValue(userStateAtom);
  const permissions = useRecoilValue(permissionsState);
  const practice = useRecoilValue(currentPractice);
  const admin_state = useRecoilValue(current_admin);
  const location = useLocation();
  const { locationState } = useSmartNavigate();

  const hasUser = !!(userState.logged_in || admin_state.logged_in);
  const { generalPreferences, isLoading } = useUserPreferencesState(
    hasUser ? ['user_general'] : []
  );
  const defaultRoute = generalPreferences.defaultRoute;

  const redirectState =
    locationState === null
      ? {
          returnTo: location
        }
      : { returnTo: locationState?.returnTo };

  const redirectTo = () => {
    if (admin_state.logged_in) return '/admin';
    if (practice.id) return '/portal';
    if (userState.logged_in) return '/myportal';
    return '/login';
  };

  if (type === 'private') return <Navigate state={redirectState} to={redirectTo()} />;

  if (type === 'admin') {
    return admin_state.logged_in ? (
      <Outlet />
    ) : (
      <Navigate state={redirectState} to="/admin/login" />
    );
  }

  // Safety net for the admin practice viewport: an admin-only session that lands
  // on a `/portal/...` route (a data-driven link, a shared component that builds
  // portal paths) would otherwise bounce to the login screen. Send it to the
  // viewport equivalent instead.
  //
  // Deliberately requires an admin session with NO practice-user session, so it
  // cannot fire for a practice user or for an admin impersonating one — both of
  // those have `userState.logged_in === true` and belong in the portal.
  if (!userState.logged_in && admin_state.logged_in && location.pathname.startsWith('/portal')) {
    const viewportPracticeId = getLastViewportPracticeId();
    if (viewportPracticeId) {
      return (
        <Navigate
          to={`/admin/practices/${viewportPracticeId}${location.pathname.slice('/portal'.length)}${
            location.search
          }${location.hash}`}
          replace
        />
      );
    }
  }

  if (!userState.logged_in) return <Navigate state={redirectState} to="/login" />;

  if (type === 'portal') {
    if (!practice.id) return <Navigate state={redirectState} to="/login" />;

    const isAtPortalRoot = location.pathname === '/portal' || location.pathname === '/portal/';

    if (isLoading && isAtPortalRoot) {
      return (
        <div className="flex h-screen w-full items-center justify-center">
          <Spinner />
        </div>
      );
    }

    // Redirect to default route if configured
    if (isAtPortalRoot && defaultRoute) {
      return <Navigate state={redirectState} to={`/portal/${defaultRoute}`} replace />;
    }

    const requiredPermission = getPortalRoutePermission(location.pathname);
    if (requiredPermission) {
      if (!permissions.loaded) return <Spinner />;
      if (!pm(permissions, requiredPermission)) return <Navigate to="/portal/dashboard" replace />;
    }

    return <Outlet />;
  }

  return <Outlet />;
};

export default PrivateRoutes;
